import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { App } from './App';
import { isNative, restoreSession } from './platform/host';
import { startTheming } from './platform/theme';
import './i18n';
import './styles/tokens.css';
import './styles/base.css';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // The server is on the same LAN, so a failed request means something is
      // actually wrong rather than flaky mobile connectivity.
      retry: 1,
    },
  },
});

// Before the first render: a dark-theme user should never see a white frame.
startTheming();

const container = document.getElementById('root');
if (!container) throw new Error('#root not found');

const root = createRoot(container);

// Installable from the browser. Skipped inside the Android application, where
// the page is already local and a worker in front of it only adds a second
// copy to keep up to date.
if (!isNative && import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' });
  });
}

// Nothing renders until the stored session has been read back. The very first
// thing the application does is ask who is signed in, and that request has to
// be able to carry the token — otherwise somebody who never signed out is shown
// the login screen every time they open the application. In a browser this
// resolves immediately and there is nothing to wait for.
void restoreSession().then(() =>
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>,
  ),
);
